import React, { useEffect, useState, useRef } from "react";
import {
  Select,
  MenuItem,
  TextField,
  IconButton,
  FormControl,
  Box,
} from "@mui/material";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import InputAdornment from "@mui/material/InputAdornment";
import { CronChooser } from "../cronChooser";
import {
  fToC,
  prefersFahrenheit,
  snapToF,
  temperatureUnit,
} from "../temperature";

export interface Setting {
  key: string;
  value: number | string;
}

// `unit` may be a function so that preference-dependent units (e.g. °C/°F)
// are resolved at render time, once custInfo is available.
interface SettingKeyDef {
  label: string;
  key: string;
  type: "dropdown" | "text" | "cron";
  options?: { label: string; value: number }[];
  unit?: string | (() => string);
  parser?: (s: string) => number;
  formatter?: (val: number) => number;
}

const SETTING_KEYS: SettingKeyDef[] = [
  {
    label: "Temperature Control Mode",
    key: "temp_mode",
    type: "dropdown" as const,
    options: [
      { label: "Off", value: 0 },
      { label: "Heat", value: 1 },
      { label: "Cool", value: 2 },
    ],
  },
  {
    label: "Temperature Setpoint",
    key: "temp_setpoint",
    type: "text" as const,
    // Temperature follows the customer's preference; the unit, parser and
    // formatter resolve it at call time. The value is always stored in
    // tenths of °C; in °F mode the display uses the controller firmware's
    // snap-to-integer algorithm (see snapToF).
    unit: () => temperatureUnit(),
    parser: (s: string) =>
      prefersFahrenheit() ? Math.round(fToC(Number(s)) * 10) : Number(s) * 10,
    formatter: (val: number) =>
      prefersFahrenheit()
        ? Math.round(snapToF(val / 10) * 10) / 10
        : val / 10,
  },
  {
    label: "Sparge Mode",
    key: "sparge_mode",
    type: "dropdown" as const,
    options: [
      { label: "Off", value: 0 },
      { label: "Auto", value: 1 },
    ],
  },
  {
    label: "Sparge Setpoint",
    key: "sparge_setpoint",
    type: "text" as const,
    unit: "mV",
    parser: (s: string) => Number(s),
    formatter: (val: number) => val,
  },
  {
    label: "Pumpover Mode",
    key: "pumpov_mode",
    type: "dropdown" as const,
    options: [
      { label: "Off", value: 0 },
      { label: "Scheduled", value: 1 },
    ],
  },
  {
    label: "Pumpover Duration",
    key: "pumpov_duration",
    type: "text" as const,
    unit: "min",
    parser: (s: string) => Number(s),
    formatter: (val: number) => val,
  },
  {
    label: "Pumpover Schedule",
    key: "pumpov_cron",
    type: "cron" as const,
  },
];

export const SettingInput = ({
  setting,
  removeable,
  readonly,
  onChange,
  onRemove,
}: {
  setting: Setting;
  removeable: boolean | null;
  readonly?: boolean;
  onChange?: (newSetting: Setting) => void;
  onRemove: () => void;
}) => {
  const selectedSettingDef = SETTING_KEYS.find((sk) => sk.key === setting.key);
  const displayValue = selectedSettingDef?.formatter
    ? selectedSettingDef.formatter(Number(setting.value))
    : setting.value;
  const unit = selectedSettingDef?.unit;

  const isCron = selectedSettingDef?.type === "cron";
  const isDropdown = selectedSettingDef?.type === "dropdown";

  const [focused, setFocused] = useState(false);
  const [rawValue, setRawValue] = useState<string>("");
  const lastInternalValue = useRef<number | null>(null);

  const handleKeyChange = (key: string) => {
    setFocused(false);
    setRawValue("");
    onChange?.({ ...setting, key, value: null });
  };

  const handleValueChange = (value: string) => {
    if (isCron) return;
    setRawValue(value);
    const parsed = parseFloat(value);
    if (value !== "" && !isNaN(parsed) && !value.endsWith(".")) {
      const def = SETTING_KEYS.find((sk) => sk.key === setting.key);
      const converted = def?.parser?.(String(parsed));
      if (converted !== undefined) {
        lastInternalValue.current = converted;
        onChange?.({ ...setting, value: converted });
      }
    }
  };

  const handleCronChange = (newValue: string) => {
    onChange?.({ ...setting, value: newValue });
  };

  const handleBlur = () => {
    if (isCron) return;
    const def = SETTING_KEYS.find((sk) => sk.key === setting.key);
    const parsed = parseFloat(rawValue);
    if (rawValue !== "" && !isNaN(parsed)) {
      const converted = def?.parser?.(String(parsed));
      onChange?.({ ...setting, value: converted });
    } else if (rawValue === "") {
      onChange?.({ ...setting, value: undefined });
    }
    setFocused(false);
    setRawValue("");
  };

  const handleFocus = () => {
    if (isCron) return;
    setFocused(true);
    setRawValue(
      String(
        displayValue !== undefined && displayValue !== null ? displayValue : "",
      ),
    );
  };

  // When the parent updates setting.value externally, reset the raw state
  useEffect(() => {
    if (isCron) return;
    if (
      lastInternalValue.current !== null &&
      lastInternalValue.current === setting.value
    ) {
      lastInternalValue.current = null;
      return;
    }
    setFocused(false);
    setRawValue("");
  }, [isCron, setting.value]);

  return (
    // flex-wrap: on a phone (where the automation columns stack to
    // full width) the key + value fields do not always fit on one row.
    <Box display="flex" alignItems="center" mb={1} gap={2} flexWrap="wrap">
      <FormControl size="small" style={{ minWidth: 200 }} disabled={readonly}>
        <Select
          value={setting.key || ""}
          onChange={(e) => handleKeyChange(e.target.value as string)}
          disabled={readonly}
          sx={{
            "& .MuiInputBase-input": {
              paddingTop: "10px",
              paddingBottom: "10px",
            },
          }}
        >
          {SETTING_KEYS.map((s) => (
            <MenuItem key={s.key} value={s.key}>
              {s.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {isCron ? (
        <Box
          style={{
            minWidth: 140,
            padding: 6,
            color: "rgb(70, 64, 66)",
            background: "rgba(0, 0, 0, 0.04)",
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
          }}
        >
          <CronChooser
            value={setting.value as string}
            setValue={(v: string) => handleCronChange(v)}
            disabled={readonly}
          />
        </Box>
      ) : isDropdown ? (
        <FormControl size="small" style={{ minWidth: 140 }} disabled={readonly}>
          <Select
            value={setting.value ?? ""}
            onChange={(e) => onChange?.({ ...setting, value: e.target.value })}
            disabled={readonly}
            sx={{
              "& .MuiInputBase-input": {
                paddingTop: "10px",
                paddingBottom: "10px",
              },
            }}
          >
            {selectedSettingDef!.options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <TextField
          style={{ width: 140 }}
          size="small"
          type={focused ? "number" : "text"}
          value={
            focused
              ? rawValue
              : displayValue !== undefined && displayValue !== null
                ? displayValue
                : ""
          }
          onChange={(e) => handleValueChange(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={readonly}
          sx={{
            "& input": { paddingTop: "10px", paddingBottom: "10px" },
          }}
          inputProps={{ min: 1, style: { textAlign: "left" } }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ fontWeight: 500 }}>
                {typeof unit === "function" ? unit() : unit}
              </InputAdornment>
            ),
          }}
        />
      )}

      {removeable ? (
        <IconButton onClick={onRemove} size="small">
          <RemoveCircleIcon />
        </IconButton>
      ) : null}
    </Box>
  );
};
